import { Controller } from "@hotwired/stimulus";
import MenuItemHandler from "./slash_menu/menu_item_handler";
import QueryHandler from "./slash_menu/query_handler";
import ProtectedNoteHandler from "./slash_menu/handlers/protected_note_handler";

export default class extends Controller {
  static targets = ["menu", "item", "textarea"];

  initialize() {
    this.menuItemHandler = new MenuItemHandler(this.itemTargets);
    this.queryHandler = new QueryHandler(this.element);
    this.handlers = [new ProtectedNoteHandler()];
    this.showingMenu = false;
    this.textArea = this.element.querySelector("textarea");

    // Initialize resize observer
    this.setupResizeObserver();
  }

  connect() {
    this.allItems = this.itemTargets.map((item) => ({
      content: item.textContent.trim(),
      element: item,
    }));
    this.hideMenu();
  }

  disconnect() {
    this.hideMenu();

    // Disconnect resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  // Set up resize observer to handle dynamic updates
  setupResizeObserver() {
    // Create a resize observer to watch for changes to the textarea and parent element
    this.resizeObserver = new ResizeObserver((_entries) => {
      // If menu is showing, update its position
      if (this.showingMenu) {
        this.updateMenuPosition();
      }
    });

    // Observe both the textarea and its parent element
    this.resizeObserver.observe(this.textArea);
    this.resizeObserver.observe(this.element);

    // Also listen for window resize events
    window.addEventListener("resize", this.updateMenuPosition.bind(this));

    // And scroll events on any parent that might scroll
    document.addEventListener(
      "scroll",
      this.updateMenuPosition.bind(this),
      true,
    );
  }

  // Update menu position based on current state
  updateMenuPosition() {
    if (!this.showingMenu) return;

    // Recalculate and update menu position
    this.positionMenuAtCursor();
  }

  toggleMenu(shouldShow) {
    shouldShow ? this.showMenu() : this.hideMenu();
  }

  filterItemsAndShowMenu() {
    const query = this.textArea.value;
    const currentQuery = this.queryHandler.getCurrentQuery(query);

    if (!this.queryHandler.hasValidQuery(query)) {
      this.hideMenu();
      return;
    }

    this.menuItemHandler.updateItemVisibility(currentQuery);
    this.updateMenuVisibility();
  }

  updateMenuVisibility() {
    const hasVisibleItems = this.menuItemHandler.getVisibleItems().length > 0;
    this.toggleMenu(hasVisibleItems);
    this.menuItemHandler.highlightFirstVisibleItem();
  }

  showMenu() {
    // Make menu visible
    this.menuTarget.classList.remove("hidden");

    // Set z-index
    this.menuTarget.style.zIndex = "9999";

    // Position the menu
    this.positionMenuAtCursor();

    this.showingMenu = true;
  }

  // Position menu at cursor or fallback to a reasonable position
  positionMenuAtCursor() {
    // Make sure we're using absolute positioning relative to the parent
    this.menuTarget.style.position = "absolute";

    // Get the current line number and column
    const text = this.textArea.value;
    const cursorPos = this.textArea.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPos);
    const lines = textBeforeCursor.split("\n");
    const currentLineNumber = lines.length;
    const currentLine = lines[lines.length - 1];

    // Get textarea styles
    const style = window.getComputedStyle(this.textArea);
    const lineHeight =
      parseInt(style.lineHeight) || parseInt(style.fontSize) * 1.2;
    const paddingTop = parseInt(style.paddingTop) || 0;
    const paddingLeft = parseInt(style.paddingLeft) || 0;

    // Calculate vertical position
    let top =
      (currentLineNumber - 1) * lineHeight +
      paddingTop -
      this.textArea.scrollTop;

    // Create a temporary span to measure text width
    const span = document.createElement("span");
    span.style.font = style.font;
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "pre";
    span.textContent = currentLine;
    document.body.appendChild(span);

    // Calculate horizontal position
    let left = span.offsetWidth + paddingLeft - this.textArea.scrollLeft;

    // Clean up
    document.body.removeChild(span);

    // Get menu dimensions
    const menuWidth = this.menuTarget.offsetWidth || 256;
    const menuHeight = this.menuTarget.offsetHeight || 200;

    // Get textarea dimensions
    const textareaWidth = this.textArea.clientWidth;
    const textareaHeight = this.textArea.clientHeight;

    // Adjust position if menu would go outside textarea
    if (left + menuWidth > textareaWidth) {
      left = Math.max(0, textareaWidth - menuWidth);
    }

    // If menu would go below textarea, position it above the current line
    if (top + menuHeight > textareaHeight) {
      top = Math.max(0, top - menuHeight);
    }

    // Add a small offset to position the menu below the cursor
    top += lineHeight;

    // Set the final position
    this.menuTarget.style.left = `${left}px`;
    this.menuTarget.style.top = `${top}px`;

    // Log position for debugging
    console.log("Menu positioned at:", {
      left,
      top,
      currentLineNumber,
      currentLine,
      textareaScroll: {
        top: this.textArea.scrollTop,
        left: this.textArea.scrollLeft,
      },
    });
  }

  hideMenu() {
    this.menuTarget.classList.add("hidden");
    this.menuItemHandler.reset();
    this.showingMenu = false;
  }

  detectSlash(event) {
    // Check if the key pressed is a slash
    if (event.key === "/") {
      // Small delay to ensure the DOM has updated with the slash character
      setTimeout(() => {
        // Focus the textarea if it's not already focused
        if (document.activeElement !== this.textArea) {
          this.textArea.focus();
        }

        // Show the menu at the current cursor position
        this.showMenu();
      }, 50);
    }
  }

  navigateMenu(event) {
    if (!this.showingMenu) return;

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      this.menuItemHandler.adjustCurrentIndex(event.key);
      this.menuItemHandler.highlightCurrentItem(
        this.menuItemHandler.getVisibleItems(),
      );
    }
  }

  clickMenuItem(event) {
    const selectMenuItem = event.currentTarget.closest(
      "[data-slash-menu-target]",
    ).dataset.slashCommand;

    const broadcastEvent = new CustomEvent("slash-command", {
      detail: {
        command: selectMenuItem,
        bucket_id: this.element.dataset.slashMenuBucketIdValue,
      },
    });
    window.dispatchEvent(broadcastEvent);

    this.hideMenu();
  }

  selectMenuItem(event) {
    const query = this.textArea.value;
    if (!this.queryHandler.hasValidQuery(query)) {
      this.hideMenu();
      return;
    }

    event.preventDefault();
    this.textArea.focus();
    this.textArea.value = "";

    const broadcastEvent = new CustomEvent("slash-command", {
      detail: {
        command: this.menuItemHandler.getSelectedMenuItem(),
        bucket_id: this.element.dataset.slashMenuBucketIdValue,
      },
    });
    window.dispatchEvent(broadcastEvent);

    this.hideMenu();
  }
}
